import { useEffect, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { ErrorNote } from "@/components/ui/panel";

/**
 * A question that must be answered before anything else on the page — "delete permanently?". Drawn as
 * an overlay of its own rather than a native `<dialog>`: the colour tokens live on `:root`, and older
 * browsers do not pass them into `::backdrop`, which would leave the scrim without a colour.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open, busy, onCancel]);

  if (!open) return null;
  return (
    <div className="bg-bank-ink/40 fixed inset-0 z-50 grid place-items-center p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="border-bank-border bg-bank-surface w-full max-w-md overflow-hidden rounded-lg border shadow-2xl"
      >
        <div className="space-y-2 p-4">
          <h2 className="text-sm font-semibold">{title}</h2>
          <div className="text-sm">{children}</div>
          <ErrorNote error={error} />
        </div>
        <div className="border-bank-border bg-bank-surface-2 flex justify-end gap-2 border-t px-4 py-2.5">
          {/* Cancel takes focus, so Enter on an accidental open does not delete anything. */}
          <Button autoFocus disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" disabled={busy} onClick={onConfirm}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
